import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { applySeo } from "../lib/seo";

// Document tracking: rev2 — Account deletion page (EN + IT): sign-in also with Google/Apple, no server-side "preferences", no claims about backups/log periods we cannot verify, note on Google/Apple accounts, link to the app privacy notice
// (rev1: Account deletion page (EN + IT))
// Published: maggio 2026

export default function DeleteAccount() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = "Delete your GozzoGuide account — GozzoGuide";
    const descTag = document.querySelector('meta[name="description"]');
    if (descTag) {
      descTag.setAttribute(
        "content",
        "How to request the deletion of your GozzoGuide account and associated personal data."
      );
    }
    window.scrollTo(0, 0);
    const restoreSeo = applySeo({ path: "/delete-account" });
    return () => {
      restoreSeo();
      if (prevTitle) document.title = prevTitle;
      if (descTag && prevDesc) descTag.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#09101F] flex flex-col" data-testid="delete-account-page">
      <Navbar minimal />

      <main className="flex-1 px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24">
        <article className="max-w-3xl mx-auto text-white/70 font-body leading-relaxed">
          {/* ===================== ENGLISH ===================== */}
          <section data-testid="delete-account-en">
            <h1
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-3"
              data-testid="delete-account-h1-en"
            >
              <span className="text-white">Delete your GozzoGuide account</span>
            </h1>
            <div className="h-1 w-24 bg-[#FF7F1A] mb-6 rounded-full" aria-hidden="true" />
            <p className="italic text-white/50 mb-8 text-sm md:text-base">
              App: GozzoGuide · Developer: Gianluca Ruocco
            </p>

            <p className="mb-8 text-base md:text-lg">
              You can permanently delete your GozzoGuide account and all the personal data associated
              with it directly from the app. The procedure is irreversible.
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              How to delete your account from the app
            </h2>
            <ol className="list-decimal pl-6 space-y-2 mb-6">
              <li>Open the GozzoGuide app on your device</li>
              <li>Sign in with your email and password, or with Google or Apple</li>
              <li>Tap &quot;Account&quot; in the bottom navigation bar</li>
              <li>Scroll down and tap &quot;Delete account&quot;</li>
              <li>Confirm the deletion when prompted</li>
            </ol>
            <p className="mb-8">
              The deletion is processed immediately. You will be signed out of the app and your
              account will no longer be accessible. Data stored only on your phone (downloaded audio and photos, language, reminders) is not on our servers: it is removed when you uninstall the app or use the “Clear” button in the Account section.
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              What gets deleted
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>Your user account (email, password, name and profile picture address if provided, sign-in method, unique user ID, and the dates of creation and last sign-in)</li>
              <li>Your consents records (privacy policy and terms of use acceptances)</li>
                          </ul>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              What is retained (and for how long)
            </h2>
            <p className="mb-4">
              For legal and accounting obligations, the following may be retained for a limited time
              after deletion:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                Security log of the authentication system (date, type of event, e-mail address and IP
                address) — kept for up to 12 months, also after deletion, for security and abuse
                prevention
              </li>
              <li>
                Technical connection logs (for example the IP address of requests) kept for short
                periods by our other service providers, as set by each of them; we do not use them to
                identify you
              </li>
              <li>
                Any technical safety copies held by our hosting provider, which expire automatically
                within 30 days
              </li>
            </ul>
            <p className="mb-8">
              After these retention periods, no data referring to your deleted account remains in our systems.
            </p>

            <p className="mb-8">
              Deleting your GozzoGuide account does not delete your Google or Apple account. If you
              signed in with Apple, you can also remove GozzoGuide from your Apple ID settings
              (Sign in with Apple).
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              Alternative request by email
            </h2>
            <p className="mb-4">
              If you can no longer access the app (lost device, forgotten password, etc.), you can
              request deletion by email at:
            </p>
            <p className="mb-4">
              <a
                href="mailto:gozzoguide@gmail.com"
                className="font-semibold text-white hover:text-[#FF7F1A] transition-colors break-all"
                data-testid="delete-account-email-en"
              >
                gozzoguide@gmail.com
              </a>
            </p>
            <p className="mb-8">
              Please include the email address used at signup. We will verify your identity and
              process the request within 30 days as required by the EU General Data Protection
              Regulation (GDPR).
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">Your rights</h2>
            <p className="mb-2">
              Under the EU GDPR you have the right to access, rectify, port, and delete your personal
              data. The full Privacy Policy is available at{" "}
              <Link to="/app-privacy" className="text-[#FF7F1A] hover:underline">
                gozzoguide.com/app-privacy
              </Link>
              .
            </p>
          </section>

          {/* Divider */}
          <hr
            className="my-16 border-0 border-t-2 border-dashed border-white/15"
            aria-hidden="true"
            data-testid="delete-account-divider"
          />

          {/* ===================== ITALIANO ===================== */}
          <section data-testid="delete-account-it">
            <h2
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-3"
              data-testid="delete-account-h1-it"
            >
              Eliminazione dell&apos;account GozzoGuide
            </h2>
            <div className="h-1 w-24 bg-[#FF7F1A] mb-6 rounded-full" aria-hidden="true" />
            <p className="italic text-white/50 mb-8 text-sm md:text-base">
              App: GozzoGuide · Sviluppatore: Gianluca Ruocco
            </p>

            <p className="mb-8 text-base md:text-lg">
              Puoi eliminare in modo permanente il tuo account GozzoGuide e tutti i dati personali
              associati direttamente dall&apos;app. La procedura è irreversibile.
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              Come eliminare l&apos;account dall&apos;app
            </h2>
            <ol className="list-decimal pl-6 space-y-2 mb-6">
              <li>Apri l&apos;app GozzoGuide sul tuo dispositivo</li>
              <li>Accedi con email e password, oppure con Google o Apple</li>
              <li>Tocca &quot;Account&quot; nella barra di navigazione in basso</li>
              <li>Scorri verso il basso e tocca &quot;Elimina account&quot;</li>
              <li>Conferma l&apos;eliminazione quando richiesto</li>
            </ol>
            <p className="mb-8">
              L&apos;eliminazione viene elaborata immediatamente. Verrai disconnesso dall&apos;app e
              l&apos;account non sarà più accessibile. I dati conservati solo sul tuo telefono (audio e foto scaricati, lingua, promemoria) non sono sui nostri server: vengono rimossi quando disinstalli l&apos;app o con il pulsante “Cancella” nella sezione Account.
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              Cosa viene eliminato
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>Il tuo account utente (email, password, nome e indirizzo della foto profilo se forniti, metodo di accesso, ID utente univoco e date di creazione e di ultimo accesso)</li>
              <li>I record dei consensi (accettazione di Privacy Policy e Termini di Uso)</li>
                          </ul>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              Cosa viene conservato (e per quanto tempo)
            </h2>
            <p className="mb-4">
              Per obblighi legali e contabili, i seguenti dati potrebbero essere conservati per un
              periodo limitato dopo l&apos;eliminazione:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                Registro di sicurezza del sistema di autenticazione (data, tipo di evento, indirizzo
                email e indirizzo IP) — conservato fino a 12 mesi, anche dopo l&apos;eliminazione, per
                sicurezza e prevenzione abusi
              </li>
              <li>
                Log tecnici di connessione (ad esempio l&apos;indirizzo IP delle richieste) conservati
                per brevi periodi dagli altri nostri fornitori di servizi, secondo quanto stabilito da
                ciascuno; non li usiamo per identificarti
              </li>
              <li>
                Eventuali copie tecniche di sicurezza detenute dal fornitore di hosting, che scadono
                automaticamente entro 30 giorni
              </li>
            </ul>
            <p className="mb-8">
              Trascorsi questi periodi di conservazione, non rimane nessun dato riferito al tuo account eliminato nei nostri sistemi.
            </p>

            <p className="mb-8">
              L&apos;eliminazione dell&apos;account GozzoGuide non elimina il tuo account Google o
              Apple. Se hai effettuato l&apos;accesso con Apple, puoi anche rimuovere GozzoGuide
              dalle impostazioni del tuo ID Apple (Accedi con Apple).
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">
              Richiesta alternativa via email
            </h2>
            <p className="mb-4">
              Se non riesci più ad accedere all&apos;app (dispositivo perso, password dimenticata,
              ecc.), puoi richiedere l&apos;eliminazione tramite email a:
            </p>
            <p className="mb-4">
              <a
                href="mailto:gozzoguide@gmail.com"
                className="font-semibold text-white hover:text-[#FF7F1A] transition-colors break-all"
                data-testid="delete-account-email-it"
              >
                gozzoguide@gmail.com
              </a>
            </p>
            <p className="mb-8">
              Includi l&apos;indirizzo email utilizzato in fase di registrazione. Verificheremo la
              tua identità e processeremo la richiesta entro 30 giorni come previsto dal Regolamento
              Generale sulla Protezione dei Dati (GDPR).
            </p>

            <h2 className="font-heading text-xl md:text-2xl text-white mt-10 mb-4">I tuoi diritti</h2>
            <p className="mb-2">
              Ai sensi del GDPR hai diritto di accesso, rettifica, portabilità e cancellazione dei
              tuoi dati personali. L&apos;informativa sulla privacy completa è disponibile su{" "}
              <Link to="/app-privacy" className="text-[#FF7F1A] hover:underline">
                gozzoguide.com/app-privacy
              </Link>
              .
            </p>
          </section>

          {/* Footer note */}
          <p
            className="text-center italic text-white/50 text-xs md:text-sm mt-16 pt-6 border-t border-white/10"
            data-testid="delete-account-last-update"
          >
            Ultimo aggiornamento: ottobre 2026
          </p>
        </article>
      </main>

      <Footer />
    </div>
  );
}
